/**
 * Route shapes and validation for KVP keys (tool / skill / user_action /
 * system_state). Which keys are *expected* is listed in kvpKeyRegistry.ts;
 * this module only describes how a matching key is stored and checked.
 */
const ALWAYS_REQUIRED = ["action", "object_type", "object_id"];

const REQUIRED_EXTRAS: Record<string, string[]> = {
    "user_action.revision_artifact": ["revision_id", "content"],
    "user_action.user_changes_artifact": ["content_diff", "tag_ids"],
    "user_action.update_artifact": ["content_diff", "tag_ids", "applied_revision_ids", "discarded_revision_ids"],
    "user_action.artifact_variable_name_update": ["variable_name"],
    "user_action.revisions_discarded_artifact": ["revision_ids", "reason"],
    "user_action.revisions_discarded_tag": ["revision_ids", "reason"],
};

const SYSTEM_STATE_REQUIRED: Record<string, string[]> = {
    "system_state.selected_objects": ["selected_objects", "open_object"],
};

export const SIDE_EFFECT_TYPES = ["create_artifact"] as const;
export type SideEffectType = (typeof SIDE_EFFECT_TYPES)[number];

export const SIDE_EFFECT_PLACEHOLDERS: Record<SideEffectType, string[]> = {
    create_artifact: ["created_artifact_id", "created_artifact_json"],
};

const CREATE_ARTIFACT_FIELDS = new Set(["type", "variable_name", "content"]);
const LEGACY_SIDE_EFFECT_FIELDS = new Set(["bind_selected_event", "select_created"]);

const I18N_STRING_FIELDS = ["variable_name", "content"] as const;

export const DEFAULT_PROMPT_SET_KEY = "system_prompt.default_prompt_set";

/** Same names as the summarisation columns of an organisation prompt set. */
export const DEFAULT_PROMPT_SET_FIELDS = [
    "summariser",
    "synthesiser",
    "document_summariser",
    "document_synthesiser",
] as const;
export type DefaultPromptSetField = (typeof DEFAULT_PROMPT_SET_FIELDS)[number];
export type DefaultPromptSetRecord = Record<DefaultPromptSetField, string>;

export type KvpRouteSpecId =
    | "default_prompt_set"
    | "user_action"
    | "system_state"
    | "tool"
    | "skill";

export type SkillRecord = {
    prompt: string;
    use_case: string;
    side_effects: Record<string, unknown>[];
};

export type UserActionRecord = {
    prompt: string;
    deleted?: string;
};

export type SystemStateRecord = {
    prompt: string;
};

export type ToolRecord = {
    description: string;
};

export type KvpValidation = {
    valid: boolean;
    errorKey?: string;
    errorVars?: Record<string, string>;
    missing?: string[];
};

const PLACEHOLDER_RE = /\{(\w+)\}/g;

export function extractPlaceholders(template: string): Set<string> {
    const found = new Set<string>();
    for (const match of template.matchAll(PLACEHOLDER_RE)) {
        found.add(match[1]);
    }
    return found;
}

export function matchRoute(key: string): KvpRouteSpecId | null {
    if (key === DEFAULT_PROMPT_SET_KEY) {
        return "default_prompt_set";
    }
    if (/^user_action\.[A-Za-z0-9_]+$/.test(key)) {
        return "user_action";
    }
    if (/^system_state\.[A-Za-z0-9_]+$/.test(key)) {
        return "system_state";
    }
    if (/^tool\.[A-Za-z0-9_]+$/.test(key)) {
        return "tool";
    }
    if (/^skill\.[A-Za-z0-9_]+$/.test(key)) {
        return "skill";
    }
    return null;
}

/**
 * Which routes edit their long text in the markdown richtext editor instead of
 * a plain textarea. Flip a route to false to fall back to the textarea if its
 * values do not survive a markdown round trip (see kvpRichText.test.ts).
 */
export const KVP_ROUTE_RICH_TEXT: Record<KvpRouteSpecId, boolean> = {
    default_prompt_set: true,
    user_action: true,
    system_state: true,
    tool: true,
    skill: true,
};

/** Keys with no route spec of their own (the free-form system_prompt.* texts). */
export function isRichTextKey(key: string): boolean {
    const spec = matchRoute(key.trim());
    if (spec) return KVP_ROUTE_RICH_TEXT[spec];
    return key.trim().startsWith("system_prompt.");
}

export function instructionI18nKey(spec: KvpRouteSpecId | null): string | null {
    switch (spec) {
        case "user_action":
            return "settings.kvp.route.userAction.help";
        case "system_state":
            return "settings.kvp.route.systemState.help";
        case "tool":
            return "settings.kvp.route.tool.help";
        case "skill":
            return "settings.kvp.route.skill.help";
        default:
            return null;
    }
}

/**
 * Placeholders the backend fills into each system prompt. `required` only lists
 * names whose absence breaks the agent (saving is blocked without them);
 * everything else is informational and listed as optional.
 */
export const SYSTEM_PROMPT_PLACEHOLDERS: Record<string, { required: string[]; optional: string[] }> = {
    "system_prompt.conversation_analysis": {
        required: [],
        optional: ["summary_length_limit", "prev_memory_section"],
    },
    "system_prompt.preference_reduction": {
        required: ["preferences"],
        optional: ["count", "cap"],
    },
    "system_prompt.ticketing_agent": {
        required: [],
        optional: ["source", "conversation_id", "user_id", "org_id"],
    },
    "system_prompt.tool_error_agent": {
        required: [],
        optional: ["agent", "failed_tools", "fingerprint", "conversation_id", "user_id", "org_id"],
    },
};

export function optionalPlaceholdersForKey(key: string): string[] {
    return SYSTEM_PROMPT_PLACEHOLDERS[key]?.optional ?? [];
}

export function requiredPlaceholdersForKey(key: string, field: "prompt" | "deleted" = "prompt"): string[] {
    const systemPrompt = SYSTEM_PROMPT_PLACEHOLDERS[key];
    if (systemPrompt) {
        return systemPrompt.required;
    }
    const spec = matchRoute(key);
    if (spec === "system_state") {
        return SYSTEM_STATE_REQUIRED[key] ?? [];
    }
    if (spec !== "user_action") return [];
    if (field === "deleted") return [...ALWAYS_REQUIRED];
    const extras = REQUIRED_EXTRAS[key] ?? [];
    return [...ALWAYS_REQUIRED, ...extras];
}

export function isCreateArtifactUserAction(key: string): boolean {
    return /^user_action\.create_artifact$/.test(key.trim());
}

export function skillIdFromKey(key: string): string | null {
    const match = key.trim().match(/^skill\.([A-Za-z0-9_]+)$/);
    return match?.[1] ?? null;
}

const RESERVED_SKILL_IDS = new Set(["help"]);

export function isReservedSkillId(skillId: string | null): boolean {
    return !!skillId && RESERVED_SKILL_IDS.has(skillId.toLowerCase());
}

/** Group node `skill.<id>` — a direct child of `skill` in the KVP tree. */
export function skillGroupIdFromPath(path: string): string | null {
    const match = path.trim().match(/^skill\.([A-Za-z0-9_]+)$/);
    return match?.[1] ?? null;
}

export function skillCommandI18nKey(skillId: string): string {
    return `skill.${skillId}.command`;
}

export function skillVariableNameI18nKey(skillId: string): string {
    return `skill.${skillId}.variableName`;
}

export function skillContentI18nKey(skillId: string): string {
    return `skill.${skillId}.content`;
}

export function isImplementedSideEffectType(type: unknown): type is SideEffectType {
    return typeof type === "string" && (SIDE_EFFECT_TYPES as readonly string[]).includes(type);
}

export function unusedSideEffectTypes(specs: Record<string, unknown>[]): SideEffectType[] {
    const used = new Set(specs.map((spec) => spec.type));
    return SIDE_EFFECT_TYPES.filter((type) => !used.has(type));
}

export function parseSideEffectSpecs(value: string): Record<string, unknown>[] | null {
    if (!value.trim()) return [];
    try {
        const parsed = JSON.parse(value) as unknown;
        const items = Array.isArray(parsed) ? parsed : [parsed];
        if (!items.every((item) => item && typeof item === "object" && !Array.isArray(item))) {
            return null;
        }
        return items as Record<string, unknown>[];
    } catch {
        return null;
    }
}

export function defaultCreateArtifactSpec(skillId: string | null): Record<string, unknown> {
    return {
        type: "create_artifact",
        variable_name: skillId ? skillVariableNameI18nKey(skillId) : "",
        content: skillId ? skillContentI18nKey(skillId) : "",
    };
}

export function normalizeSideEffectSpec(
    spec: Record<string, unknown>,
    skillId: string | null,
): Record<string, unknown> {
    if (spec.type === "create_artifact") {
        return defaultCreateArtifactSpec(skillId);
    }
    const next: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(spec)) {
        if (LEGACY_SIDE_EFFECT_FIELDS.has(field)) continue;
        next[field] = value;
    }
    return next;
}

export function normalizeSideEffectSpecs(
    specs: Record<string, unknown>[],
    skillId: string | null,
): Record<string, unknown>[] {
    const out: Record<string, unknown>[] = [];
    const seen = new Set<string>();
    for (const spec of specs) {
        const normalized = normalizeSideEffectSpec(spec, skillId);
        const type = typeof normalized.type === "string" ? normalized.type : "";
        if (isImplementedSideEffectType(type)) {
            if (seen.has(type)) continue;
            seen.add(type);
        }
        out.push(normalized);
    }
    return out;
}

export function serializeSideEffectSpecs(
    specs: Record<string, unknown>[],
    skillId?: string | null,
): string {
    const normalized = normalizeSideEffectSpecs(specs, skillId ?? null);
    if (normalized.length === 0) return "[]";
    if (normalized.length === 1) return JSON.stringify(normalized[0], null, 2);
    return JSON.stringify(normalized, null, 2);
}

export function parseSkillRecord(value: string): SkillRecord | null {
    if (!value.trim()) return { prompt: "", use_case: "", side_effects: [] };
    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const obj = parsed as Record<string, unknown>;
        const prompt = obj.prompt ?? "";
        if (typeof prompt !== "string") return null;
        const useCase = obj.use_case ?? "";
        if (typeof useCase !== "string") return null;
        let specs: unknown;
        if (obj.side_effects == null) specs = [];
        else if (Array.isArray(obj.side_effects)) specs = obj.side_effects;
        else specs = [obj.side_effects];
        if (
            !Array.isArray(specs) ||
            !specs.every((item) => item && typeof item === "object" && !Array.isArray(item))
        ) {
            return null;
        }
        return { prompt, use_case: useCase, side_effects: specs as Record<string, unknown>[] };
    } catch {
        return null;
    }
}

export function serializeSkillRecord(
    prompt: string,
    sideEffects: Record<string, unknown>[],
    skillId?: string | null,
    useCase = "",
): string {
    return JSON.stringify(
        {
            prompt,
            use_case: useCase,
            side_effects: normalizeSideEffectSpecs(sideEffects, skillId ?? null),
        },
        null,
        2,
    );
}

export function parseUserActionRecord(value: string): UserActionRecord | null {
    if (!value.trim()) return { prompt: "" };
    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const obj = parsed as Record<string, unknown>;
        const prompt = obj.prompt ?? "";
        if (typeof prompt !== "string") return null;
        const record: UserActionRecord = { prompt };
        if ("deleted" in obj) {
            const deleted = obj.deleted ?? "";
            if (typeof deleted !== "string") return null;
            record.deleted = deleted;
        }
        return record;
    } catch {
        return null;
    }
}

export function serializeUserActionRecord(prompt: string, deleted?: string): string {
    const obj: UserActionRecord = { prompt };
    if (deleted !== undefined) obj.deleted = deleted;
    return JSON.stringify(obj, null, 2);
}

export function parseSystemStateRecord(value: string): SystemStateRecord | null {
    if (!value.trim()) return { prompt: "" };
    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const obj = parsed as Record<string, unknown>;
        const prompt = obj.prompt ?? "";
        if (typeof prompt !== "string") return null;
        return { prompt };
    } catch {
        return null;
    }
}

export function serializeSystemStateRecord(prompt: string): string {
    return JSON.stringify({ prompt }, null, 2);
}

export function emptyDefaultPromptSet(): DefaultPromptSetRecord {
    return {
        summariser: "",
        synthesiser: "",
        document_summariser: "",
        document_synthesiser: "",
    };
}

export function parseDefaultPromptSetRecord(value: string): DefaultPromptSetRecord | null {
    const record = emptyDefaultPromptSet();
    if (!value.trim()) return record;
    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const obj = parsed as Record<string, unknown>;
        for (const field of DEFAULT_PROMPT_SET_FIELDS) {
            const raw = obj[field] ?? "";
            if (typeof raw !== "string") return null;
            record[field] = raw;
        }
        return record;
    } catch {
        return null;
    }
}

export function serializeDefaultPromptSetRecord(record: DefaultPromptSetRecord): string {
    const obj: Record<string, string> = {};
    for (const field of DEFAULT_PROMPT_SET_FIELDS) obj[field] = record[field] ?? "";
    return JSON.stringify(obj, null, 2);
}

export function parseToolRecord(value: string): ToolRecord | null {
    if (!value.trim()) return { description: "" };
    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const obj = parsed as Record<string, unknown>;
        const description = obj.description ?? "";
        if (typeof description !== "string") return null;
        return { description };
    } catch {
        return null;
    }
}

export function serializeToolRecord(description: string): string {
    return JSON.stringify({ description }, null, 2);
}

export function placeholdersForSideEffectType(type: unknown): string[] {
    if (!isImplementedSideEffectType(type)) return [];
    return SIDE_EFFECT_PLACEHOLDERS[type];
}

export function placeholdersFromSideEffects(
    specs: Record<string, unknown>[] | undefined,
): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const spec of specs ?? []) {
        for (const name of placeholdersForSideEffectType(spec.type)) {
            if (seen.has(name)) continue;
            seen.add(name);
            out.push(name);
        }
    }
    return out;
}

export function i18nDependenciesForKey(key: string, value: string): string[] {
    const spec = matchRoute(key);
    const skillId = skillIdFromKey(key);
    const deps: string[] = [];
    if (spec === "skill" && skillId) {
        deps.push(`skill.${skillId}.command`);
        const record = parseSkillRecord(value);
        const specs = record?.side_effects ?? [];
        for (const item of specs) {
            if (item.type === "create_artifact") {
                deps.push(skillVariableNameI18nKey(skillId), skillContentI18nKey(skillId));
            }
            for (const field of I18N_STRING_FIELDS) {
                const raw = item[field];
                if (typeof raw === "string" && raw.trim()) deps.push(raw.trim());
            }
        }
    }
    return [...new Set(deps)];
}

export function kvpLeavesForI18nKey(
    i18nKey: string,
    rows: { key: string; value: string }[],
): string[] {
    const out: string[] = [];
    const command = i18nKey.match(/^skill\.([A-Za-z0-9_]+)\.command$/);
    if (command) out.push(`skill.${command[1]}`);
    const field = i18nKey.match(/^skill\.([A-Za-z0-9_]+)\.(?:variableName|content)$/);
    if (field) out.push(`skill.${field[1]}`);
    for (const row of rows) {
        if (matchRoute(row.key) !== "skill") continue;
        if (i18nDependenciesForKey(row.key, row.value).includes(i18nKey)) {
            out.push(row.key);
        }
    }
    return [...new Set(out)];
}

export function isParentOfExisting(key: string, allKeys: string[]): boolean {
    const prefix = `${key}.`;
    return allKeys.some((other) => other !== key && other.startsWith(prefix));
}

function validateSideEffectsJson(value: string): KvpValidation {
    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    } catch {
        return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
    }
    const items = Array.isArray(parsed) ? parsed : [parsed];
    if (items.length === 0) {
        return { valid: true };
    }
    for (const item of items) {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
        const obj = item as Record<string, unknown>;
        if (obj.type !== "create_artifact") {
            return { valid: false, errorKey: "settings.kvp.validation.unknownSideEffect" };
        }
        for (const field of Object.keys(obj)) {
            if (!CREATE_ARTIFACT_FIELDS.has(field) && !LEGACY_SIDE_EFFECT_FIELDS.has(field)) {
                return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
            }
        }
        for (const field of I18N_STRING_FIELDS) {
            const raw = obj[field];
            if (raw == null || raw === "") continue;
            if (typeof raw !== "string") {
                return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
            }
        }
    }
    return { valid: true };
}

export function validateKvpEntry(key: string, value: string, allKeys: string[]): KvpValidation {
    const stripped = key.trim();
    if (!stripped) {
        return { valid: false, errorKey: "settings.kvp.keyEmpty" };
    }
    if (isParentOfExisting(stripped, allKeys)) {
        return { valid: false, errorKey: "settings.kvp.validation.parentKey" };
    }
    const spec = matchRoute(stripped);
    if (spec === "default_prompt_set") {
        if (parseDefaultPromptSetRecord(value) === null) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
    }
    if (spec === "user_action") {
        const record = parseUserActionRecord(value);
        if (record === null) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
        const required = requiredPlaceholdersForKey(stripped);
        const present = extractPlaceholders(record.prompt);
        const missing = required.filter((name) => !present.has(name));
        if (missing.length) {
            return {
                valid: false,
                errorKey: "settings.kvp.validation.missingPlaceholders",
                errorVars: { refs: missing.map((n) => `{${n}}`).join(", ") },
                missing,
            };
        }
        if (record.deleted !== undefined) {
            const deletedRequired = requiredPlaceholdersForKey(stripped, "deleted");
            const deletedPresent = extractPlaceholders(record.deleted);
            const deletedMissing = deletedRequired.filter((name) => !deletedPresent.has(name));
            if (deletedMissing.length) {
                return {
                    valid: false,
                    errorKey: "settings.kvp.validation.missingPlaceholders",
                    errorVars: { refs: deletedMissing.map((n) => `{${n}}`).join(", ") },
                    missing: deletedMissing,
                };
            }
        }
    }
    if (spec === "system_state") {
        const record = parseSystemStateRecord(value);
        if (record === null) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
        const required = requiredPlaceholdersForKey(stripped);
        const present = extractPlaceholders(record.prompt);
        const missing = required.filter((name) => !present.has(name));
        if (missing.length) {
            return {
                valid: false,
                errorKey: "settings.kvp.validation.missingPlaceholders",
                errorVars: { refs: missing.map((n) => `{${n}}`).join(", ") },
                missing,
            };
        }
    }
    if (spec === "tool") {
        const record = parseToolRecord(value);
        if (record === null) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
    }
    if (spec === "skill") {
        const skillId = skillIdFromKey(stripped);
        if (isReservedSkillId(skillId)) {
            return { valid: false, errorKey: "settings.kvp.validation.reservedSkillId" };
        }
        const record = parseSkillRecord(value);
        if (record === null) {
            return { valid: false, errorKey: "settings.kvp.validation.invalidJson" };
        }
        if (record.side_effects.length === 0) return { valid: true };
        return validateSideEffectsJson(JSON.stringify(record.side_effects));
    }
    if (SYSTEM_PROMPT_PLACEHOLDERS[stripped]) {
        const present = extractPlaceholders(value);
        const missing = requiredPlaceholdersForKey(stripped).filter((name) => !present.has(name));
        if (missing.length) {
            return {
                valid: false,
                errorKey: "settings.kvp.validation.missingPlaceholders",
                errorVars: { refs: missing.map((n) => `{${n}}`).join(", ") },
                missing,
            };
        }
    }
    return { valid: true };
}
